import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useCreators } from "@/context/CreatorsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { SocialIcon } from "@/components/ui/LinkIcon";
import { 
  ArrowLeft, 
  Plus, 
  Trash2, 
  GripVertical,
  Save,
  Image as ImageIcon,
  ChevronRight,
  Settings,
  X,
  Camera,
  Instagram,
  Youtube,
  ExternalLink
} from "lucide-react";
import { toast } from "sonner";

const iconOptions = [
  { value: "globe", label: "Web/General" },
  { value: "onlyfans", label: "OnlyFans" },
  { value: "playboy", label: "Playboy" },
  { value: "mym", label: "MYM" },
  { value: "instagram", label: "Instagram" },
  { value: "twitter", label: "Twitter/X" },
  { value: "tiktok", label: "TikTok" },
  { value: "telegram", label: "Telegram" },
  { value: "youtube", label: "YouTube" },
  { value: "lock", label: "Privado/VIP" },
  { value: "mail", label: "Email" },
  { value: "shopping-bag", label: "Tienda" },
];

const sizeOptions = [
  { value: "large", label: "Grande" },
  { value: "medium", label: "Mediano" },
  { value: "small", label: "Pequeño" },
];

const imageDisplayOptions = [
  { value: "square", label: "Cuadrada" },
  { value: "horizontal", label: "Horizontal" },
  { value: "vertical", label: "Vertical" },
];

const colorPresets = [
  { bg: "#1a1a1a", text: "#ffffff" },
  { bg: "#dc2626", text: "#ffffff" },
  { bg: "#ea580c", text: "#ffffff" },
  { bg: "#ca8a04", text: "#000000" },
  { bg: "#16a34a", text: "#ffffff" },
  { bg: "#0891b2", text: "#ffffff" },
  { bg: "#2563eb", text: "#ffffff" },
  { bg: "#7c3aed", text: "#ffffff" },
  { bg: "#db2777", text: "#ffffff" },
];

const socialPlatforms = [
  { key: "onlyfans", label: "OnlyFans", color: "#00AFF0" },
  { key: "playboy", label: "Playboy", color: "#000000" },
  { key: "mym", label: "MYM", color: "#000000" },
  { key: "instagram", label: "Instagram", color: "#E4405F" },
  { key: "twitter", label: "Twitter", color: "#1DA1F2" },
  { key: "tiktok", label: "TikTok", color: "#000000" },
  { key: "telegram", label: "Telegram", color: "#0088cc" },
  { key: "youtube", label: "YouTube", color: "#FF0000" },
];

const AdminCreatorForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);
  const fileInputRef = useRef(null);
  const linkImageRef = useRef(null);
  const galleryInputRef = useRef(null);
  
  const { 
    getCreatorById, 
    addCreator, 
    updateCreator, 
    isUsernameAvailable,
    fetchCreatorById
  } = useCreators();

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    username: "",
    bio: "",
    avatar: "",
    coverImage: "",
    coverVideo: "",
    links: [],
    socialLinks: {},
    galleryImages: [],
    telegramVip: {
      enabled: false,
      monthlyPrice: 0,
      lifetimePrice: 0,
      paymentInfo: "",
      paymentMethods: [],
      vipChannelLink: ""
    },
    leadCapture: {
      enabled: false,
      title: "¿Quieres recibir nuestro Top 10 de modelos?",
      description: "Suscríbete a nuestro boletín gratis y recibe lo mejor cada semana.",
      buttonText: "Suscribirme gratis",
      rewardUrl: "",
      rewardMessage: "Gracias por suscribirte. Pronto recibirás novedades.",
      requireEmail: true,
      requireWhatsapp: false
    }
  });

  // UI State
  const [activeSection, setActiveSection] = useState(null);
  const [editingLink, setEditingLink] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Section toggles
  const [sections, setSections] = useState({
    bio: true,
    featuredLinks: true,
    gallery: false
  });

  // Load creator data if editing
  useEffect(() => {
    const loadCreator = async () => {
      if (!isEditing) return;
      
      // First try local state, then fetch from server
      let creator = getCreatorById(id);
      
      if (!creator) {
        creator = await fetchCreatorById(id);
      }
      
      if (creator) {
        setFormData({
          name: creator.name || "",
          username: creator.username || "",
          bio: creator.bio || "",
          avatar: creator.avatar || "",
          coverImage: creator.coverImage || "",
          coverVideo: creator.coverVideo || "",
          links: creator.links || [],
          socialLinks: creator.socialLinks || {},
          galleryImages: creator.galleryImages || [],
          telegramVip: creator.telegramVip || {
            enabled: false,
            monthlyPrice: 0,
            lifetimePrice: 0,
            paymentInfo: "",
            paymentMethods: []
          },
          leadCapture: creator.leadCapture || {
            enabled: false,
            title: "¿Quieres recibir nuestro Top 10 de modelos?",
            description: "Suscríbete a nuestro boletín gratis y recibe lo mejor cada semana.",
            buttonText: "Suscribirme gratis",
            rewardUrl: "",
            rewardMessage: "Gracias por suscribirte. Pronto recibirás novedades.",
            requireEmail: true,
            requireWhatsapp: false
          }
        });
      } else {
        toast.error("Creadora no encontrada");
        navigate("/admin/creadoras");
      }
    };
    
    loadCreator();
  }, [id, isEditing, navigate]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle social link changes
  const handleSocialLinkChange = (platform, value) => {
    setFormData(prev => ({
      ...prev,
      socialLinks: {
        ...prev.socialLinks,
        [platform]: value
      }
    }));
  };

  // Compress image before upload
  const compressImage = (file, maxWidth = 800) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          const compressed = canvas.toDataURL('image/jpeg', 0.8);
          resolve(compressed);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  // Handle image upload with compression
  const handleImageUpload = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Por favor selecciona una imagen válida");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("La imagen no debe superar los 10MB");
      return;
    }

    try {
      toast.loading("Procesando imagen...", { id: 'image-upload' });
      const compressed = await compressImage(file, field === 'coverImage' ? 1200 : 600);
      setFormData(prev => ({ ...prev, [field]: compressed }));
      toast.success("Imagen cargada", { id: 'image-upload' });
    } catch (error) {
      toast.error("Error al procesar la imagen", { id: 'image-upload' });
    }
  };

  // Handle video upload - chunked for large files
  const handleVideoUpload = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('video/')) {
      toast.error("Por favor selecciona un video válido");
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      toast.error("El video no debe superar los 50MB");
      return;
    }

    const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
    
    try {
      toast.loading("Subiendo video...", { id: 'video-upload' });
      
      if (file.size > 5 * 1024 * 1024) {
        // Chunked upload for files > 5MB
        const initRes = await fetch(`${apiUrl}/api/upload/video/init`, { method: 'POST' });
        const { upload_id } = await initRes.json();
        
        const chunkSize = 2 * 1024 * 1024; // 2MB chunks
        const totalChunks = Math.ceil(file.size / chunkSize);
        
        for (let i = 0; i < totalChunks; i++) {
          const start = i * chunkSize;
          const end = Math.min(start + chunkSize, file.size);
          const chunk = file.slice(start, end);
          const fd = new FormData();
          fd.append('file', chunk, file.name);
          
          await fetch(`${apiUrl}/api/upload/video/chunk/${upload_id}`, {
            method: 'POST', body: fd
          });
          
          const pct = Math.round(((i + 1) / totalChunks) * 100);
          toast.loading(`Subiendo video... ${pct}%`, { id: 'video-upload' });
        }
        
        toast.loading("Procesando video...", { id: 'video-upload' });
        const finalRes = await fetch(`${apiUrl}/api/upload/video/finalize/${upload_id}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ filename: file.name })
        });
        
        if (finalRes.ok) {
          const data = await finalRes.json();
          setFormData(prev => ({ ...prev, [field]: data.url }));
          toast.success("Video subido y procesado", { id: 'video-upload' });
        } else {
          toast.error("Error al procesar el video", { id: 'video-upload' });
        }
      } else {
        // Direct upload for small files
        const fd = new FormData();
        fd.append('file', file);
        const res = await fetch(`${apiUrl}/api/upload/video`, { method: 'POST', body: fd });
        if (res.ok) {
          const data = await res.json();
          setFormData(prev => ({ ...prev, [field]: data.url }));
          toast.success("Video subido", { id: 'video-upload' });
        } else {
          toast.error("Error al subir el video", { id: 'video-upload' });
        }
      }
    } catch (error) {
      toast.error("Error al subir el video", { id: 'video-upload' });
    }
  };

  // Handle gallery upload (images + videos)
  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    for (const file of files) {
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      
      if (!isImage && !isVideo) {
        toast.error("Solo imágenes o videos");
        continue;
      }

      if (isImage) {
        if (file.size > 5 * 1024 * 1024) { toast.error("Imagen max 5MB"); continue; }
        const reader = new FileReader();
        reader.onload = () => {
          setFormData(prev => ({
            ...prev,
            galleryImages: [...prev.galleryImages, {
              id: Date.now().toString() + Math.random(),
              url: reader.result,
              type: "image"
            }]
          }));
        };
        reader.readAsDataURL(file);
      }

      if (isVideo) {
        if (file.size > 50 * 1024 * 1024) { toast.error("Video max 50MB"); continue; }
        const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
        try {
          toast.loading("Subiendo video...", { id: `gallery-video-${file.name}` });
          if (file.size > 5 * 1024 * 1024) {
            const initRes = await fetch(`${apiUrl}/api/upload/video/init`, { method: 'POST' });
            const { upload_id } = await initRes.json();
            const chunkSize = 2 * 1024 * 1024;
            const totalChunks = Math.ceil(file.size / chunkSize);
            for (let i = 0; i < totalChunks; i++) {
              const chunk = file.slice(i * chunkSize, Math.min((i + 1) * chunkSize, file.size));
              const fd = new FormData(); fd.append('file', chunk, file.name);
              await fetch(`${apiUrl}/api/upload/video/chunk/${upload_id}`, { method: 'POST', body: fd });
              toast.loading(`Video ${Math.round(((i+1)/totalChunks)*100)}%`, { id: `gallery-video-${file.name}` });
            }
            toast.loading("Procesando...", { id: `gallery-video-${file.name}` });
            const finalRes = await fetch(`${apiUrl}/api/upload/video/finalize/${upload_id}`, {
              method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify({filename: file.name})
            });
            if (finalRes.ok) {
              const data = await finalRes.json();
              setFormData(prev => ({...prev, galleryImages: [...prev.galleryImages, { id: Date.now().toString(), url: data.url, type: "video" }]}));
              toast.success("Video agregado", { id: `gallery-video-${file.name}` });
            }
          } else {
            const fd = new FormData(); fd.append('file', file);
            const res = await fetch(`${apiUrl}/api/upload/video`, { method: 'POST', body: fd });
            if (res.ok) {
              const data = await res.json();
              setFormData(prev => ({...prev, galleryImages: [...prev.galleryImages, { id: Date.now().toString(), url: data.url, type: "video" }]}));
              toast.success("Video agregado", { id: `gallery-video-${file.name}` });
            }
          }
        } catch { toast.error("Error al subir video", { id: `gallery-video-${file.name}` }); }
      }
    }
  };

  // Remove gallery image
  const removeGalleryImage = (imageId) => {
    setFormData(prev => ({
      ...prev,
      galleryImages: prev.galleryImages.filter(img => img.id !== imageId)
    }));
  };

  // Handle link changes
  const handleLinkChange = (field, value) => {
    if (editingLink === null) return;
    const newLinks = [...formData.links];
    // popOnLoad is exclusive: only one link can have it active
    if (field === "popOnLoad" && value === true) {
      newLinks.forEach((l, i) => {
        if (i !== editingLink) newLinks[i] = { ...l, popOnLoad: false };
      });
    }
    newLinks[editingLink] = { ...newLinks[editingLink], [field]: value };
    setFormData(prev => ({ ...prev, links: newLinks }));
  };

  // Handle link image upload
  const handleLinkImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file || editingLink === null) return;

    const reader = new FileReader();
    reader.onload = () => {
      handleLinkChange('image', reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Add new link
  const addLink = () => {
    const newLink = {
      id: Date.now().toString(),
      title: "Nuevo Enlace",
      url: "",
      icon: "globe",
      image: "",
      imageDisplay: "square",
      size: "medium",
      bgColor: "#1a1a1a",
      textColor: "#ffffff",
      featured: formData.links.length === 0
    };
    setFormData(prev => ({ ...prev, links: [...prev.links, newLink] }));
    setEditingLink(formData.links.length);
  };

  // Remove link
  const removeLink = (index) => {
    const newLinks = formData.links.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, links: newLinks }));
    setEditingLink(null);
  };

  // Toggle section
  const toggleSection = (section) => {
    setSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Basic validation
    if (!formData.name.trim()) {
      toast.error("El nombre es requerido");
      return;
    }
    if (!formData.username.trim()) {
      toast.error("El username es requerido");
      return;
    }
    if (!isUsernameAvailable(formData.username, isEditing ? id : null)) {
      toast.error("Este username ya está en uso");
      return;
    }

    setIsSubmitting(true);

    try {
      const cleanedSocialLinks = Object.fromEntries(
        Object.entries(formData.socialLinks).filter(([_, v]) => v && v.trim())
      );

      const dataToSave = {
        ...formData,
        socialLinks: cleanedSocialLinks,
        coverImage: formData.coverImage || formData.avatar
      };

      if (isEditing) {
        await updateCreator(id, dataToSave);
        toast.success("Perfil actualizado");
      } else {
        await addCreator(dataToSave);
        toast.success("Perfil creado");
      }
      navigate("/admin/creadoras");
    } catch (error) {
      toast.error(error.message || "Error al guardar");
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentLink = editingLink !== null ? formData.links[editingLink] : null;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Top Bar */}
      <header className="fixed top-0 left-0 right-0 h-14 bg-[#0a0a0a] border-b border-white/10 flex items-center justify-between px-4 z-50">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate("/admin/creadoras")}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <span className="font-medium">Editar Perfil</span>
        </div>
        
        <div className="flex items-center gap-2 px-3 py-1.5 bg-white/10 rounded-full text-sm">
          <span className="text-white/60">Tu Bio Link</span>
          <span className="font-medium">jglinks.com/{formData.username || 'username'}</span>
        </div>

        <div className="flex items-center gap-3">
          {isEditing && formData.username && (
            <Button
              onClick={() => navigate(`/admin/creadoras/${formData.username}/auto-posting`)}
              variant="outline"
              data-testid="open-autopost-btn"
              className="border-white/20 hover:bg-white/10 rounded-full px-4 text-xs"
            >
              📤 Auto-posting Telegram
            </Button>
          )}
          <Button 
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="bg-primary hover:bg-primary/90 rounded-full px-6"
          >
            {isSubmitting ? "Guardando..." : "Guardar"}
          </Button>
        </div>
      </header>

      <div className="flex pt-14 min-h-screen">
        {/* Left Side - Live Preview */}
        <div className="w-1/2 border-r border-white/10 flex items-start justify-center p-8 bg-[#111]">
          <div className="w-full max-w-sm">
            {/* Phone Frame Preview */}
            <div className="bg-black rounded-[2.5rem] p-2 shadow-2xl">
              <div className="bg-[#0a0a0a] rounded-[2rem] overflow-hidden">
                {/* Preview Content */}
                <div className="relative">
                  {/* Cover/Avatar Section */}
                  <div className="relative h-72 bg-gradient-to-b from-white/5 to-transparent">
                    {formData.avatar ? (
                      <img 
                        src={formData.avatar} 
                        alt="" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-white/10 to-white/5" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
                  </div>

                  {/* Profile Info */}
                  <div className="relative -mt-20 px-6 pb-6 text-center">
                    <h2 className="text-2xl font-bold text-white mb-1">
                      {formData.name || "Nombre"}
                    </h2>
                    
                    {/* Change Profile Picture Link */}
                    <button 
                      onClick={() => fileInputRef.current?.click()}
                      className="text-sm text-white/60 hover:text-white/80 mb-1"
                    >
                      Cambiar Foto de Perfil
                    </button>
                    
                    <p className="text-white/50 text-sm mb-4">
                      @{formData.username || "username"}
                    </p>

                    {/* Social Icons */}
                    <div className="flex items-center justify-center gap-2 mb-4">
                      {Object.entries(formData.socialLinks).filter(([_, v]) => v).map(([platform]) => (
                        <div 
                          key={platform}
                          className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center"
                        >
                          <SocialIcon platform={platform} className="w-4 h-4" />
                        </div>
                      ))}
                      {Object.keys(formData.socialLinks).filter(k => formData.socialLinks[k]).length === 0 && (
                        <>
                          <div className="w-9 h-9 rounded-full bg-pink-500/80 flex items-center justify-center">
                            <Plus className="w-4 h-4" />
                          </div>
                          <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-yellow-500 via-pink-500 to-purple-500 flex items-center justify-center">
                            <Instagram className="w-4 h-4" />
                          </div>
                          <div className="w-9 h-9 rounded-full bg-red-600 flex items-center justify-center">
                            <Youtube className="w-4 h-4" />
                          </div>
                        </>
                      )}
                    </div>

                    {/* Bio Preview */}
                    {sections.bio && formData.bio && (
                      <p className="text-white/60 text-sm mb-6">{formData.bio}</p>
                    )}

                    {/* Links Preview */}
                    {sections.featuredLinks && formData.links.length > 0 && (
                      <div className="space-y-3">
                        {formData.links.map((link, idx) => (
                          <div 
                            key={link.id}
                            onClick={() => setEditingLink(idx)}
                            className={`rounded-xl overflow-hidden cursor-pointer transition-all ${
                              editingLink === idx ? 'ring-2 ring-primary' : ''
                            }`}
                            style={{ backgroundColor: link.bgColor || '#1a1a1a' }}
                          >
                            {link.image && link.size !== 'small' ? (
                              <div className={`relative ${
                                link.imageDisplay === 'vertical' ? 'aspect-[3/4]' :
                                link.imageDisplay === 'horizontal' ? 'aspect-video' :
                                link.size === 'large' ? 'aspect-video' : 'aspect-[3/1]'
                              }`}>
                                <img src={link.image} alt="" className="w-full h-full object-cover" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                                <div className="absolute bottom-3 left-3 right-3">
                                  <span className="font-medium" style={{ color: link.textColor }}>
                                    {link.title}
                                  </span>
                                </div>
                              </div>
                            ) : (
                              <div className="px-4 py-3 text-center">
                                <span className="font-medium" style={{ color: link.textColor }}>
                                  {link.title}
                                </span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Editor Panel */}
        <div className="w-1/2 overflow-y-auto" style={{ height: 'calc(100vh - 56px)' }}>
          <div className="p-6 space-y-4">
            {/* Hidden file inputs */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => handleImageUpload(e, 'avatar')}
              className="hidden"
            />
            <input
              id="adminCoverInput"
              type="file"
              accept="image/*"
              onChange={(e) => handleImageUpload(e, 'coverImage')}
              className="hidden"
            />
            <input
              ref={linkImageRef}
              type="file"
              accept="image/*"
              onChange={handleLinkImageUpload}
              className="hidden"
            />
            <input
              ref={galleryInputRef}
              type="file"
              accept="image/*,video/*"
              multiple
              onChange={handleGalleryUpload}
              className="hidden"
            />

            {/* Profile Images Section */}
            <div className="bg-white/5 rounded-2xl p-4 space-y-4">
              <div className="flex items-center gap-3">
                <Camera className="w-4 h-4 text-white/50" />
                <span className="font-medium flex-1">Imágenes del Perfil</span>
              </div>
              
              <div className="grid grid-cols-2 gap-3 pl-7">
                {/* Avatar Upload */}
                <div>
                  <Label className="text-xs text-white/50 mb-2 block">Foto de Perfil</Label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full aspect-square rounded-xl border-2 border-dashed border-white/20 hover:border-white/40 transition-colors overflow-hidden relative group"
                    >
                      {formData.avatar ? (
                        <>
                          <img src={formData.avatar} alt="Avatar" className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <Camera className="w-6 h-6 text-white" />
                          </div>
                        </>
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-white/40">
                          <Camera className="w-8 h-8 mb-2" />
                          <span className="text-xs">Subir foto</span>
                        </div>
                      )}
                    </button>
                    {formData.avatar && (
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, avatar: "" }))}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center text-white transition-colors"
                        title="Eliminar foto"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Cover Upload */}
                <div>
                  <Label className="text-xs text-white/50 mb-2 block">Foto Banner</Label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => document.getElementById('adminCoverInput')?.click()}
                      className="w-full aspect-square rounded-xl border-2 border-dashed border-white/20 hover:border-white/40 transition-colors overflow-hidden relative group"
                    >
                      {formData.coverImage ? (
                        <>
                          <img src={formData.coverImage} alt="Cover" className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <Camera className="w-6 h-6 text-white" />
                          </div>
                        </>
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-white/40">
                          <ImageIcon className="w-8 h-8 mb-2" />
                          <span className="text-xs">Subir banner</span>
                        </div>
                      )}
                    </button>
                    {formData.coverImage && (
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, coverImage: "" }))}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center text-white transition-colors"
                        title="Eliminar banner"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Cover Video Upload */}
                <div>
                  <Label className="text-xs text-white/50 mb-2 block">Video de fondo</Label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => document.getElementById('adminCoverVideoInput')?.click()}
                      className="w-full aspect-square rounded-xl border-2 border-dashed border-white/20 hover:border-white/40 transition-colors overflow-hidden relative group"
                    >
                      {formData.coverVideo ? (
                        <>
                          <video src={formData.coverVideo} muted autoPlay loop playsInline className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <Camera className="w-6 h-6 text-white" />
                          </div>
                        </>
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-white/40">
                          <Camera className="w-8 h-8 mb-2" />
                          <span className="text-xs">Subir video</span>
                        </div>
                      )}
                    </button>
                    {formData.coverVideo && (
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, coverVideo: "" }))}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center text-white transition-colors"
                        title="Eliminar video"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                  <input
                    id="adminCoverVideoInput"
                    type="file"
                    accept="video/*"
                    className="hidden"
                    onChange={(e) => handleVideoUpload(e, 'coverVideo')}
                  />
                </div>
              </div>
            </div>

            {/* Basic Info Section */}
            <div className="bg-white/5 rounded-2xl p-4 space-y-4">
              <div className="flex items-center gap-3">
                <GripVertical className="w-4 h-4 text-white/30" />
                <span className="font-medium flex-1">Información Básica</span>
              </div>
              
              <div className="space-y-3 pl-7">
                <div>
                  <Label className="text-xs text-white/50">Nombre</Label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Nombre de la modelo"
                    className="mt-1 bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div>
                  <Label className="text-xs text-white/50">Username</Label>
                  <Input
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    placeholder="username"
                    className="mt-1 bg-white/5 border-white/10 text-white"
                  />
                </div>
              </div>
            </div>

            {/* Bio Section */}
            <div className="bg-white/5 rounded-2xl p-4">
              <div className="flex items-center gap-3">
                <GripVertical className="w-4 h-4 text-white/30" />
                <span className="font-medium flex-1">Bio</span>
                <ChevronRight className="w-4 h-4 text-white/30" />
                <Switch 
                  checked={sections.bio} 
                  onCheckedChange={() => toggleSection('bio')}
                />
              </div>
              
              {sections.bio && (
                <div className="mt-4 pl-7">
                  <Textarea
                    name="bio"
                    value={formData.bio}
                    onChange={handleChange}
                    placeholder="Añade una bio a tu perfil"
                    className="bg-white/5 border-white/10 text-white resize-none"
                    rows={3}
                  />
                </div>
              )}
            </div>

            {/* Social Links Section */}
            <div className="bg-white/5 rounded-2xl p-4">
              <div className="flex items-center gap-3 mb-4">
                <GripVertical className="w-4 h-4 text-white/30" />
                <span className="font-medium flex-1">Redes Sociales</span>
                <ChevronRight className="w-4 h-4 text-white/30" />
              </div>
              
              <div className="space-y-3 pl-7">
                {socialPlatforms.map(platform => (
                  <div key={platform.key} className="flex items-center gap-3">
                    <div 
                      className="w-8 h-8 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: platform.color }}
                    >
                      <SocialIcon platform={platform.key} className="w-4 h-4 text-white" />
                    </div>
                    <Input
                      value={formData.socialLinks[platform.key] || ""}
                      onChange={(e) => handleSocialLinkChange(platform.key, e.target.value)}
                      placeholder={`URL de ${platform.label}`}
                      className="flex-1 bg-white/5 border-white/10 text-white text-sm"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Featured Links Section */}
            <div className="bg-white/5 rounded-2xl p-4">
              <div className="flex items-center gap-3 mb-4">
                <GripVertical className="w-4 h-4 text-white/30" />
                <span className="font-medium flex-1">Enlaces Destacados</span>
                <Switch 
                  checked={sections.featuredLinks} 
                  onCheckedChange={() => toggleSection('featuredLinks')}
                />
              </div>
              
              {sections.featuredLinks && (
                <div className="space-y-3 pl-7">
                  {/* Existing Links */}
                  {formData.links.map((link, idx) => (
                    <div 
                      key={link.id}
                      onClick={() => setEditingLink(idx)}
                      className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all ${
                        editingLink === idx 
                          ? 'bg-primary/20 border border-primary' 
                          : 'bg-white/5 hover:bg-white/10'
                      }`}
                    >
                      <GripVertical className="w-4 h-4 text-white/30" />
                      {link.image ? (
                        <img src={link.image} alt="" className="w-10 h-10 rounded-lg object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                          <SocialIcon platform={link.icon} className="w-5 h-5 text-white/50" />
                        </div>
                      )}
                      <span className="flex-1 font-medium truncate">{link.title}</span>
                      <button 
                        onClick={(e) => { e.stopPropagation(); removeLink(idx); }}
                        className="p-1 hover:bg-white/10 rounded"
                      >
                        <Trash2 className="w-4 h-4 text-white/50" />
                      </button>
                    </div>
                  ))}

                  {/* Add New Link Button */}
                  <button
                    onClick={addLink}
                    className="w-full py-3 rounded-xl border-2 border-dashed border-white/20 hover:border-white/40 transition-colors flex items-center justify-center gap-2 text-white/60 hover:text-white/80"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Nuevo Enlace</span>
                  </button>
                </div>
              )}
            </div>

            {/* Gallery Section */}
            <div className="bg-white/5 rounded-2xl p-4">
              <div className="flex items-center gap-3 mb-4">
                <GripVertical className="w-4 h-4 text-white/30" />
                <span className="font-medium flex-1">Galería</span>
                <Switch 
                  checked={sections.gallery} 
                  onCheckedChange={() => toggleSection('gallery')}
                />
              </div>
              
              {sections.gallery && (
                <div className="pl-7 space-y-4">
                  {/* Gallery Grid */}
                  {formData.galleryImages.length > 0 && (
                    <div className="grid grid-cols-3 gap-2">
                      {formData.galleryImages.map((img) => (
                        <div key={img.id} className="relative aspect-square rounded-lg overflow-hidden group">
                          {img.type === 'video' ? (
                            <video src={img.url} muted autoPlay loop playsInline className="w-full h-full object-cover" />
                          ) : (
                            <img src={img.url} alt="" className="w-full h-full object-cover" />
                          )}
                          <button
                            onClick={() => removeGalleryImage(img.id)}
                            className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3 text-white" />
                          </button>
                          {img.type === 'video' && (
                            <div className="absolute bottom-1 left-1 bg-black/60 px-1.5 py-0.5 rounded text-[10px] text-white">VIDEO</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Upload Button */}
                  <button
                    onClick={() => galleryInputRef.current?.click()}
                    className="w-full py-4 rounded-xl border-2 border-dashed border-white/20 hover:border-white/40 transition-colors flex flex-col items-center justify-center gap-2 text-white/60 hover:text-white/80"
                  >
                    <Camera className="w-6 h-6" />
                    <span className="text-sm">Subir fotos o videos</span>
                    <span className="text-xs text-white/40">Puedes seleccionar varios</span>
                  </button>
                  
                  <p className="text-xs text-white/40 text-center">
                    {formData.galleryImages.length} archivos en la galería
                  </p>
                </div>
              )}
            </div>

            {/* Lead Capture Section */}
            <div className="bg-gradient-to-br from-pink-500/10 to-orange-500/10 rounded-2xl p-4 border border-pink-500/20">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-full bg-pink-500/20 flex items-center justify-center">
                  <svg className="w-4 h-4 text-pink-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 12 20 22 4 22 4 12" />
                    <rect x="2" y="7" width="20" height="5" />
                    <line x1="12" y1="22" x2="12" y2="7" />
                    <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
                    <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-pink-400">Captación de Leads</p>
                  <p className="text-white/40 text-xs">Recolecta emails y WhatsApp ofreciendo contenido gratis</p>
                </div>
                <Switch
                  data-testid="lead-capture-toggle"
                  checked={formData.leadCapture?.enabled || false}
                  onCheckedChange={(checked) => setFormData(prev => ({
                    ...prev,
                    leadCapture: { ...prev.leadCapture, enabled: checked }
                  }))}
                />
              </div>

              {formData.leadCapture?.enabled && (
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-white/50 mb-1 block">Título</Label>
                    <Input
                      data-testid="lead-capture-title-input"
                      value={formData.leadCapture?.title || ""}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        leadCapture: { ...prev.leadCapture, title: e.target.value }
                      }))}
                      placeholder="Contenido exclusivo gratis"
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-white/50 mb-1 block">Descripción</Label>
                    <Input
                      data-testid="lead-capture-description-input"
                      value={formData.leadCapture?.description || ""}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        leadCapture: { ...prev.leadCapture, description: e.target.value }
                      }))}
                      placeholder="Regístrate y recibe contenido exclusivo..."
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-white/50 mb-1 block">Texto del botón</Label>
                    <Input
                      value={formData.leadCapture?.buttonText || ""}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        leadCapture: { ...prev.leadCapture, buttonText: e.target.value }
                      }))}
                      placeholder="Quiero contenido gratis"
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-white/50 mb-1 block">URL de recompensa (opcional)</Label>
                    <Input
                      value={formData.leadCapture?.rewardUrl || ""}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        leadCapture: { ...prev.leadCapture, rewardUrl: e.target.value }
                      }))}
                      placeholder="https://t.me/canal_secreto o link a Drive..."
                      className="bg-white/5 border-white/10 text-white"
                    />
                    <p className="text-white/30 text-[10px] mt-1">
                      Si lo dejas vacío, solo se mostrará el mensaje de gracias.
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-white/50 mb-1 block">Mensaje de gracias</Label>
                    <Input
                      value={formData.leadCapture?.rewardMessage || ""}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        leadCapture: { ...prev.leadCapture, rewardMessage: e.target.value }
                      }))}
                      placeholder="¡Gracias! Pronto recibirás contenido exclusivo."
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>
                  <div className="flex items-center justify-between bg-white/5 rounded-lg p-3">
                    <span className="text-xs text-white/70">Email obligatorio</span>
                    <Switch
                      checked={formData.leadCapture?.requireEmail !== false}
                      onCheckedChange={(checked) => setFormData(prev => ({
                        ...prev,
                        leadCapture: { ...prev.leadCapture, requireEmail: checked }
                      }))}
                    />
                  </div>
                  <div className="flex items-center justify-between bg-white/5 rounded-lg p-3">
                    <span className="text-xs text-white/70">WhatsApp obligatorio</span>
                    <Switch
                      checked={!!formData.leadCapture?.requireWhatsapp}
                      onCheckedChange={(checked) => setFormData(prev => ({
                        ...prev,
                        leadCapture: { ...prev.leadCapture, requireWhatsapp: checked }
                      }))}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Telegram VIP Section */}
            <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-2xl p-4 border border-blue-500/20">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <svg className="w-4 h-4 text-blue-400" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z"/>
                  </svg>
                </div>
                <span className="font-medium flex-1 text-blue-400">Telegram VIP</span>
                <Switch 
                  checked={formData.telegramVip?.enabled || false}
                  onCheckedChange={(checked) => setFormData(prev => ({
                    ...prev,
                    telegramVip: { ...prev.telegramVip, enabled: checked }
                  }))}
                />
              </div>
              
              {formData.telegramVip?.enabled && (
                <div className="space-y-4">
                  {/* VIP channel link — sent automatically to buyers on approval */}
                  <div className="p-3 rounded-xl bg-gradient-to-br from-pink-500/10 to-rose-500/5 border border-pink-400/30">
                    <Label className="text-xs text-pink-200 font-bold mb-1.5 block flex items-center gap-1.5">
                      🔗 Link del canal VIP (auto-envío)
                    </Label>
                    <Input
                      type="text"
                      value={formData.telegramVip?.vipChannelLink || ""}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        telegramVip: { ...prev.telegramVip, vipChannelLink: e.target.value }
                      }))}
                      className="bg-black/40 border-white/10 text-white"
                      placeholder="https://t.me/+AbCdEf123..."
                      data-testid="vip-channel-link-input"
                    />
                    <p className="text-[11px] text-pink-100/60 mt-1.5 leading-relaxed">
                      Pegá el link de invitación permanente del canal VIP de esta modelo.
                      Cuando apruebes un pago, el bot lo envía automáticamente al comprador.
                      Es el mismo link para todos los miembros.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-white/50 mb-1 block">Precio Mensual ($USD)</Label>
                      <Input
                        type="number"
                        value={formData.telegramVip?.monthlyPrice || ""}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          telegramVip: { ...prev.telegramVip, monthlyPrice: parseFloat(e.target.value) || 0 }
                        }))}
                        className="bg-white/5 border-white/10 text-white"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-white/50 mb-1 block">Precio Lifetime ($USD)</Label>
                      <Input
                        type="number"
                        value={formData.telegramVip?.lifetimePrice || ""}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          telegramVip: { ...prev.telegramVip, lifetimePrice: parseFloat(e.target.value) || 0 }
                        }))}
                        className="bg-white/5 border-white/10 text-white"
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  {/* Offer / Discount Section */}
                  <div className="bg-yellow-500/5 rounded-xl border border-yellow-500/20 p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm text-yellow-400 font-medium">Oferta / Descuento</Label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.telegramVip?.offerEnabled || false}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            telegramVip: { ...prev.telegramVip, offerEnabled: e.target.checked }
                          }))}
                          className="rounded"
                        />
                        <span className="text-xs text-white/50">Activar oferta</span>
                      </label>
                    </div>

                    {formData.telegramVip?.offerEnabled && (
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs text-white/50 mb-1 block">Precio Oferta Mensual ($USD)</Label>
                            <Input
                              type="number"
                              value={formData.telegramVip?.offerMonthlyPrice || ""}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                telegramVip: { ...prev.telegramVip, offerMonthlyPrice: parseFloat(e.target.value) || 0 }
                              }))}
                              className="bg-white/5 border-white/10 text-white"
                              placeholder="0.00"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-white/50 mb-1 block">Precio Oferta Lifetime ($USD)</Label>
                            <Input
                              type="number"
                              value={formData.telegramVip?.offerLifetimePrice || ""}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                telegramVip: { ...prev.telegramVip, offerLifetimePrice: parseFloat(e.target.value) || 0 }
                              }))}
                              className="bg-white/5 border-white/10 text-white"
                              placeholder="0.00"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs text-white/50 mb-1 block">Fecha fin de oferta</Label>
                            <Input
                              type="date"
                              value={formData.telegramVip?.offerEndDate ? formData.telegramVip.offerEndDate.split('T')[0] : ""}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                telegramVip: { ...prev.telegramVip, offerEndDate: e.target.value ? new Date(e.target.value).toISOString() : null }
                              }))}
                              className="bg-white/5 border-white/10 text-white"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-white/50 mb-1 block">Etiqueta oferta</Label>
                            <Input
                              value={formData.telegramVip?.offerLabel || ""}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                telegramVip: { ...prev.telegramVip, offerLabel: e.target.value }
                              }))}
                              className="bg-white/5 border-white/10 text-white"
                              placeholder="Ej: OFERTA ESPECIAL -50%"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Global payment methods notice */}
                  <div className="bg-blue-500/5 rounded-xl border border-blue-500/20 p-3">
                    <p className="text-blue-400 text-xs">
                      Los métodos de pago son globales. Configúralos desde <a href="/admin/metodos-pago" className="underline font-medium">Métodos de Pago</a>
                    </p>
                  </div>

                  {/* Legacy payment info (hidden but kept for compatibility) */}
                  <input 
                    type="hidden" 
                    value={formData.telegramVip?.paymentInfo || ""} 
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Sidebar - Link Editor Panel */}
        {editingLink !== null && currentLink && (
          <div className="fixed right-0 top-14 bottom-0 w-96 bg-[#0f0f0f] border-l border-white/10 overflow-y-auto z-40">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-semibold text-lg">Editar Enlace</h3>
                <button 
                  onClick={() => setEditingLink(null)}
                  className="p-2 hover:bg-white/10 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Link Preview */}
              <div 
                className="rounded-xl overflow-hidden mb-6"
                style={{ backgroundColor: currentLink.bgColor }}
              >
                {(currentLink.video || currentLink.image) ? (
                  <div className="relative aspect-video">
                    {currentLink.video ? (
                      <video src={currentLink.video} muted autoPlay loop playsInline className="w-full h-full object-cover" />
                    ) : (
                      <img src={currentLink.image} alt="" className="w-full h-full object-cover" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                    <div className="absolute bottom-3 left-3">
                      <span className="font-medium" style={{ color: currentLink.textColor }}>
                        {currentLink.title}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="px-6 py-4 text-center">
                    <span className="font-medium" style={{ color: currentLink.textColor }}>
                      {currentLink.title}
                    </span>
                  </div>
                )}
              </div>

              {/* Size Options */}
              <div className="mb-6">
                <Label className="text-xs text-white/50 mb-3 block">Tamaño del enlace</Label>
                <div className="grid grid-cols-3 gap-2">
                  {sizeOptions.map(size => (
                    <button
                      key={size.value}
                      onClick={() => handleLinkChange('size', size.value)}
                      className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                        currentLink.size === size.value
                          ? 'bg-primary text-white'
                          : 'bg-white/5 text-white/60 hover:bg-white/10'
                      }`}
                    >
                      {size.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Image Upload */}
              <div className="mb-4">
                <Label className="text-xs text-white/50 mb-3 block">Imagen del enlace</Label>
                <button
                  onClick={() => linkImageRef.current?.click()}
                  className="w-full py-3 rounded-xl border-2 border-dashed border-white/20 hover:border-white/40 transition-colors flex items-center justify-center gap-2 text-white/60 hover:text-white/80"
                >
                  <Camera className="w-4 h-4" />
                  <span>{currentLink.image ? 'Cambiar imagen' : 'Subir imagen'}</span>
                </button>
              </div>

              {/* Video Upload for Link */}
              <div className="mb-6">
                <Label className="text-xs text-white/50 mb-3 block">Video del enlace (reemplaza imagen)</Label>
                <div className="flex gap-2">
                  <button
                    onClick={() => document.getElementById('linkVideoInput')?.click()}
                    className="flex-1 py-3 rounded-xl border-2 border-dashed border-white/20 hover:border-white/40 transition-colors flex items-center justify-center gap-2 text-white/60 hover:text-white/80"
                  >
                    <Camera className="w-4 h-4" />
                    <span>{currentLink.video ? 'Cambiar video' : 'Subir video'}</span>
                  </button>
                  {currentLink.video && (
                    <button
                      onClick={() => handleLinkChange('video', '')}
                      className="px-3 py-3 rounded-xl bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <input
                  id="linkVideoInput"
                  type="file"
                  accept="video/*"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (file.size > 50 * 1024 * 1024) { toast.error("Max 50MB"); return; }
                    const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
                    try {
                      toast.loading("Subiendo video...", { id: 'link-video' });
                      if (file.size > 5 * 1024 * 1024) {
                        const initRes = await fetch(`${apiUrl}/api/upload/video/init`, { method: 'POST' });
                        const { upload_id } = await initRes.json();
                        const chunkSize = 2 * 1024 * 1024;
                        const totalChunks = Math.ceil(file.size / chunkSize);
                        for (let i = 0; i < totalChunks; i++) {
                          const chunk = file.slice(i * chunkSize, Math.min((i + 1) * chunkSize, file.size));
                          const fd = new FormData();
                          fd.append('file', chunk, file.name);
                          await fetch(`${apiUrl}/api/upload/video/chunk/${upload_id}`, { method: 'POST', body: fd });
                          toast.loading(`Subiendo... ${Math.round(((i + 1) / totalChunks) * 100)}%`, { id: 'link-video' });
                        }
                        toast.loading("Procesando...", { id: 'link-video' });
                        const finalRes = await fetch(`${apiUrl}/api/upload/video/finalize/${upload_id}`, {
                          method: 'POST', headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ filename: file.name })
                        });
                        if (finalRes.ok) {
                          const data = await finalRes.json();
                          handleLinkChange('video', data.url);
                          toast.success("Video listo", { id: 'link-video' });
                        }
                      } else {
                        const fd = new FormData();
                        fd.append('file', file);
                        const res = await fetch(`${apiUrl}/api/upload/video`, { method: 'POST', body: fd });
                        if (res.ok) {
                          const data = await res.json();
                          handleLinkChange('video', data.url);
                          toast.success("Video subido", { id: 'link-video' });
                        }
                      }
                    } catch { toast.error("Error al subir", { id: 'link-video' }); }
                  }}
                />
              </div>

              {/* Image Display Options - Only show when image exists */}
              {currentLink.image && (
                <div className="mb-6">
                  <Label className="text-xs text-white/50 mb-3 block">Formato de imagen</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {imageDisplayOptions.map(option => (
                      <button
                        key={option.value}
                        onClick={() => handleLinkChange('imageDisplay', option.value)}
                        className={`py-2 px-3 rounded-lg text-sm font-medium transition-all flex flex-col items-center gap-1 ${
                          (currentLink.imageDisplay || 'square') === option.value
                            ? 'bg-primary text-white'
                            : 'bg-white/5 text-white/60 hover:bg-white/10'
                        }`}
                      >
                        <div className={`bg-white/20 ${
                          option.value === 'square' ? 'w-6 h-6' :
                          option.value === 'horizontal' ? 'w-8 h-5' :
                          'w-5 h-8'
                        }`} />
                        <span className="text-xs">{option.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Icon Select */}
              <div className="mb-6">
                <Label className="text-xs text-white/50 mb-2 block">Icono</Label>
                <select
                  value={currentLink.icon}
                  onChange={(e) => handleLinkChange('icon', e.target.value)}
                  className="w-full h-10 rounded-lg bg-white/5 border border-white/10 text-white px-3"
                >
                  {iconOptions.map(opt => (
                    <option key={opt.value} value={opt.value} className="bg-[#1a1a1a]">
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* URL */}
              <div className="mb-6">
                <Label className="text-xs text-white/50 mb-2 block">URL del enlace</Label>
                <Input
                  value={currentLink.url}
                  onChange={(e) => handleLinkChange('url', e.target.value)}
                  placeholder="https://..."
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>

              {/* Title */}
              <div className="mb-6">
                <Label className="text-xs text-white/50 mb-2 block">Título</Label>
                <Input
                  value={currentLink.title}
                  onChange={(e) => handleLinkChange('title', e.target.value)}
                  placeholder="Título del enlace"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>

              {/* Badge text (only meaningful when featured) */}
              <div className="mb-6">
                <Label className="text-xs text-white/50 mb-2 block">
                  Texto destacado <span className="text-white/30">(opcional, solo en enlace destacado)</span>
                </Label>
                <Input
                  value={currentLink.badge || ""}
                  onChange={(e) => handleLinkChange('badge', e.target.value)}
                  placeholder="Ej: ¡OFERTA DE LANZAMIENTO! 🔥"
                  maxLength={40}
                  data-testid="link-badge-input"
                  className="bg-white/5 border-white/10 text-white"
                />
                <p className="text-[10px] text-white/40 mt-1">Máx 40 caracteres. Se anima con un brillo llamativo.</p>
              </div>

              {/* +18 (NSFW) toggle */}
              <div className="mb-4">
                <button
                  type="button"
                  onClick={() => handleLinkChange('nsfw', !currentLink.nsfw)}
                  data-testid="link-nsfw-toggle"
                  className={`w-full flex items-center justify-between gap-3 p-3 rounded-xl border transition-all ${
                    currentLink.nsfw
                      ? 'bg-red-500/10 border-red-500/40 text-red-300'
                      : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10'
                  }`}
                >
                  <div className="flex items-center gap-2 text-left">
                    <span className="text-lg">🔞</span>
                    <div>
                      <div className="text-sm font-semibold">Contenido +18</div>
                      <div className="text-[10px] opacity-70">Fuerza apertura fuera de Instagram</div>
                    </div>
                  </div>
                  <div className={`w-10 h-6 rounded-full relative transition-all ${currentLink.nsfw ? 'bg-red-500' : 'bg-white/20'}`}>
                    <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${currentLink.nsfw ? 'left-[18px]' : 'left-0.5'}`} />
                  </div>
                </button>
              </div>

              {/* Pop-on-load toggle */}
              <div className="mb-6">
                <button
                  type="button"
                  onClick={() => handleLinkChange('popOnLoad', !currentLink.popOnLoad)}
                  data-testid="link-pop-on-load-toggle"
                  className={`w-full flex items-center justify-between gap-3 p-3 rounded-xl border transition-all ${
                    currentLink.popOnLoad
                      ? 'bg-amber-500/10 border-amber-500/40 text-amber-300'
                      : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10'
                  }`}
                >
                  <div className="flex items-center gap-2 text-left">
                    <span className="text-lg">🎯</span>
                    <div>
                      <div className="text-sm font-semibold">Mostrar como pop-up al entrar</div>
                      <div className="text-[10px] opacity-70">Aparece grande sobre el perfil. Solo 1 link a la vez.</div>
                    </div>
                  </div>
                  <div className={`w-10 h-6 rounded-full relative transition-all ${currentLink.popOnLoad ? 'bg-amber-500' : 'bg-white/20'}`}>
                    <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${currentLink.popOnLoad ? 'left-[18px]' : 'left-0.5'}`} />
                  </div>
                </button>
              </div>

              {/* Color Customization */}
              <div className="mb-6">
                <Label className="text-xs text-white/50 mb-3 block">Personalizar Color</Label>
                <div className="flex gap-2 flex-wrap">
                  {colorPresets.map((color, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        handleLinkChange('bgColor', color.bg);
                        handleLinkChange('textColor', color.text);
                      }}
                      className={`w-8 h-8 rounded-full border-2 transition-all ${
                        currentLink.bgColor === color.bg 
                          ? 'border-white scale-110' 
                          : 'border-transparent hover:scale-105'
                      }`}
                      style={{ backgroundColor: color.bg }}
                    />
                  ))}
                </div>
              </div>

              {/* Custom Color Inputs */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div>
                  <Label className="text-xs text-white/50 mb-2 block">Color Fondo</Label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={currentLink.bgColor}
                      onChange={(e) => handleLinkChange('bgColor', e.target.value)}
                      className="w-10 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={currentLink.bgColor}
                      onChange={(e) => handleLinkChange('bgColor', e.target.value)}
                      className="flex-1 bg-white/5 border-white/10 text-white text-xs"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-white/50 mb-2 block">Color Texto</Label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={currentLink.textColor}
                      onChange={(e) => handleLinkChange('textColor', e.target.value)}
                      className="w-10 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={currentLink.textColor}
                      onChange={(e) => handleLinkChange('textColor', e.target.value)}
                      className="flex-1 bg-white/5 border-white/10 text-white text-xs"
                    />
                  </div>
                </div>
              </div>

              {/* Delete Link */}
              <button
                onClick={() => removeLink(editingLink)}
                className="w-full py-3 rounded-xl bg-red-500/10 text-red-500 hover:bg-red-500/20 transition-colors flex items-center justify-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                <span>Eliminar Enlace</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCreatorForm;
